// src/pages/AdminManagerReview.jsx
// STATUS: active | UPDATED: 2026-09-19
// LOG:
//   2026-09-19  New. The thing standing between us and the manager level.
//
// Jazz settling his own manager system, thirty nine lines, three buttons.
//
// WHY THIS IS NOT A NOTE OR A CONVERSATION.
//
// We asked him the same question in prose twice, once in an email and once in a
// note, and prose gets read and not answered. It is also a genuinely hard thing
// to answer in prose, because "is this still how you run a floor" is really
// thirty nine separate questions and he would have to hold all of them at once.
//
// So: one line at a time, and the default action is a single click. He does not
// type unless he actually wants to change something, which matters because he
// dictates almost everything and typing is the thing he avoids.
//
// Nothing here is a grade. Every line came out of HIS manual, so a dropped item
// is us finding out the extract is stale rather than him getting something
// wrong. The copy has to keep saying that.

import { useCallback, useEffect, useState } from 'react'
import {
  Box, Button, HStack, VStack, Text, Textarea, Spinner, Alert, AlertIcon, Badge, Progress,
} from '@chakra-ui/react'
import { Check, Pencil, X, RotateCcw } from 'lucide-react'
import { listReview, decide, SECTIONS } from '../lib/managerReview'
import { useAuth } from '../hooks/useAuth'

function Item({ row, onDecided }) {
  const { user } = useAuth()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(row.corrected_text || row.source_text)
  const [busy, setBusy] = useState(null)
  const [error, setError] = useState(null)

  const settle = async (status, correctedText) => {
    setBusy(status)
    setError(null)
    const { data, error: err } = await decide({ id: row.id, status, correctedText, userId: user?.id })
    setBusy(null)
    if (err) return setError(typeof err === 'string' ? err : err.message)
    setEditing(false)
    onDecided(data)
  }

  const settled = row.status !== 'pending'
  const tone = { confirmed: 'green', corrected: 'orange', dropped: 'gray' }[row.status]

  return (
    <Box py={4} borderTop="1px solid" borderColor="line" opacity={row.status === 'dropped' ? 0.55 : 1}>
      <HStack align="flex-start" justify="space-between" spacing={4} flexWrap="wrap">
        <Box flex={1} minW="240px">
          <Text
            fontSize="body"
            lineHeight={1.55}
            maxW="measure"
            textDecoration={row.status === 'dropped' ? 'line-through' : 'none'}
          >
            {row.source_text}
          </Text>
          {row.corrected_text && (
            <Box mt={2} borderLeft="3px solid" borderColor="accent" pl={3}>
              <Text fontSize="sm" color="inkDim" mb={0.5}>
                You changed it to
              </Text>
              <Text fontSize="body" maxW="measure">
                {row.corrected_text}
              </Text>
            </Box>
          )}
        </Box>
        {settled && <Badge variant="subtle" colorScheme={tone}>{row.status}</Badge>}
      </HStack>

      {error && (
        <Alert status="error" borderRadius="md" fontSize="sm" mt={3}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {editing ? (
        <VStack align="stretch" spacing={2} mt={3} maxW="measure">
          <Textarea
            id={`fix-${row.id}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            fontSize="body"
            placeholder="How you actually run it"
          />
          <HStack>
            <Button size="sm" isLoading={busy === 'corrected'} onClick={() => settle('corrected', draft)}>
              Save my wording
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </HStack>
        </VStack>
      ) : (
        <HStack spacing={2} mt={3} flexWrap="wrap">
          {settled ? (
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<RotateCcw size={14} />}
              isLoading={busy === 'pending'}
              onClick={() => settle('pending')}
            >
              Undo
            </Button>
          ) : (
            <>
              <Button size="sm" leftIcon={<Check size={15} />} isLoading={busy === 'confirmed'} onClick={() => settle('confirmed')}>
                Still right
              </Button>
              <Button size="sm" variant="outline" leftIcon={<Pencil size={14} />} onClick={() => setEditing(true)}>
                Change it
              </Button>
              <Button size="sm" variant="ghost" leftIcon={<X size={14} />} isLoading={busy === 'dropped'} onClick={() => settle('dropped')}>
                We do not do this
              </Button>
            </>
          )}
        </HStack>
      )}
    </Box>
  )
}

export default function AdminManagerReview() {
  const [state, setState] = useState({ loading: true, rows: [], error: null })

  const load = useCallback(() => {
    listReview().then(({ data, error }) =>
      setState({ loading: false, rows: data, error: error ? error.message : null })
    )
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const onDecided = (row) =>
    setState((s) => ({ ...s, rows: s.rows.map((r) => (r.id === row.id ? row : r)) }))

  const total = state.rows.length
  const settled = state.rows.filter((r) => r.status !== 'pending').length

  return (
    <VStack align="stretch" spacing={8} maxW="sheet">
      <Box>
        <Text textStyle="pageEyebrow" mb={1}>
          From your Trainer Edition
        </Text>
        <Text textStyle="pageTitle">Your manager system</Text>
        <Text fontSize="lg" color="inkMuted" mt={2} maxW="measure" lineHeight={1.55}>
          Every line below came out of your own 2026 manual. None of it was written by us. The only
          question is whether it is still how you run a floor, and the answer to most of them should be
          one click.
        </Text>
      </Box>

      {state.error && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {state.error}
        </Alert>
      )}

      {state.loading ? (
        <HStack spacing={3} color="inkMuted">
          <Spinner size="sm" />
          <Text fontSize="body">Loading</Text>
        </HStack>
      ) : (
        <>
          <Box>
            <HStack justify="space-between" align="baseline" mb={2}>
              <Text fontSize="body" color="inkMuted">
                {settled === total
                  ? 'All settled. The manager level can be built on this.'
                  : `${settled} of ${total} settled`}
              </Text>
              <Text fontSize="sm" color="inkDim" fontFamily="mono">
                {total ? Math.round((settled / total) * 100) : 0}%
              </Text>
            </HStack>
            {/* No colorScheme. The theme sets filledTrack bg to ink in baseStyle,
                which wins over a colorScheme, so passing one looks deliberate
                and does nothing. */}
            <Progress value={total ? (settled / total) * 100 : 0} size="sm" borderRadius="full" />
          </Box>

          {SECTIONS.map((sec) => {
            const rows = state.rows.filter((r) => r.section === sec.key)
            if (rows.length === 0) return null
            return (
              <Box key={sec.key}>
                <Text fontSize="lg" fontWeight={600} mb={0.5}>
                  {sec.title}
                </Text>
                <Text fontSize="body" color="inkMuted" mb={1} maxW="measure">
                  {sec.note}
                </Text>
                {rows.map((row) => (
                  <Item key={row.id} row={row} onDecided={onDecided} />
                ))}
              </Box>
            )
          })}
        </>
      )}
    </VStack>
  )
}
